import { ActionButton } from '@/common/buttons/ActionButton';
import { EntityPropList } from '@/common/entities/entityProp/EntityPropList';
import { ExportList } from '@/common/entities/export/ExportList';
import { EntityDetail } from '@/common/entityFramework/entityDetail/EntityDetail';
import { EntityForm } from '@/common/entityFramework/entityForm/EntityForm';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { JobList } from '@/skdr/entities/job/JobList';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect } from 'react';
import { McdInvRbtLedgerPymtList } from '../mcdInvRbtLedgerPymt/McdInvRbtLedgerPymtList';
import { LedgerList } from './LedgerList';

/**
 * Ledgers are linked to a Payment through a per-domain join entity rather than a
 * pymtId column on the ledger itself, so each domain resolves its ledger ids from
 * its own join entity and foreign key field.
 */
const LEDGER_PYMT_SPECS = {
  MCD: { entity: 'MCD_INV_RBT_LEDGER_PYMT', ledgerIdField: 'mcdLedgerId' },
  MCO: { entity: 'MCO_INV_RBT_LEDGER_PYMT', ledgerIdField: 'mcoLedgerId' },
  CHBK: { entity: 'INDIR_SALE_CHBK_LEDGER_PYMT', ledgerIdField: 'chbkLedgerId' },
  INCTV: { entity: 'INCTV_RBT_LEDGER_PYMT', ledgerIdField: 'inctvLedgerId' },
};

export const PymtDetail = () => {
  const { entityObj } = useEntityMgr();

  const rsrc = useRsrc();

  /**
   * This code is not good. Please do not copy this. Particularly, the insertion of
   * ledgerIds into the entityObj is not a good pattern.
   *
   * TODO: refactor
   */
  useEffect(() => {
    const domain = entityObj.domain;
    if (domain === undefined) {
      return;
    }

    const ledgerPymtSpec = LEDGER_PYMT_SPECS[domain];
    if (ledgerPymtSpec === undefined) {
      return;
    }

    rsrc
      .rows('sc', ledgerPymtSpec.entity, { expr: ExprUtils.whereParams('pymtId', entityObj.id) })
      .then((ledgerPymts) => {
        // A ledger has one join row per rebate/installment, so ids are deduped
        const ledgerIds = [...new Set(ledgerPymts.map((ledgerPymt) => ledgerPymt[ledgerPymtSpec.ledgerIdField]))];
        entityObj.ledgerIds = ledgerIds;
      });
  }, [entityObj.domain]);

  // Once PymtDetail is refactored into separate components per domain, this will not be necessary
  const secondTab =
    entityObj.domain === 'MCD' ?
      {
        id: 'Invoices and Ledgers',
        label: 'Invoices & Ledgers',
        content: <McdInvRbtLedgerPymtList pymtId={entityObj.id} />,
      }
    : { id: 'ledgers', label: 'Ledgers', content: <LedgerList pymtObj={entityObj} /> }; // TODO: discuss if LedgerList should even exist for the other domains

  const tabSpecs = [
    { id: 'infoTab', label: 'Info', content: <EntityForm /> },
    secondTab,
    { id: 'export', label: 'Exports', content: <ExportList parentEntity="PYMT" parentEntityId={entityObj.id} /> },
    {
      id: 'approvals',
      label: 'Approvals',
      disabled: !entityObj.apprJobId,
      content: <JobList chain jobId={entityObj.apprJobId} />,
    },
    {
      id: 'entityProperties',
      label: 'Entity Properties',
      content: <EntityPropList parentEntity="PYMT" parentEntityObj={entityObj} />,
    },
  ];

  let lifecycleActionPluginCode;
  const domain = entityObj.domain;

  if (domain === 'MCD') {
    lifecycleActionPluginCode = 'MCD_PYMT_PROC_MGR_ACTION_PLUGIN';
  } else if (domain === 'CHBK') {
    lifecycleActionPluginCode = 'CHBK_PYMT_PROC_MGR_ACTION_PLUGIN';
  } else if (domain === 'INCTV') {
    lifecycleActionPluginCode = 'INCTV_PYMT_PROC_MGR_ACTION_PLUGIN';
  }

  const lifecycleData = {
    title: 'Payment Lifecycle',
    entity: 'PYMT',
    entityId: entityObj.id,
    pluginCode: lifecycleActionPluginCode,
  };

  const augButtons = (buttons) => {
    buttons.push(<ActionButton data={lifecycleData} toolTip="Run Lifecycle" icon={fas.faPlay} />);
  };

  return <EntityDetail tabSpecs={tabSpecs} augButtons={augButtons} />;
};
